import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  addDaysLocal,
  eachLocalDay,
  formatDateLocal,
  startOfWeekMonday,
} from '../utils/scheduleWeek';

type MobileScheduleStatus =
  | 'working'
  | 'day_off'
  | 'mothers_day'
  | 'remote'
  | 'sick_leave'
  | 'rest_day'
  | 'state_holiday'
  | 'business_trip'
  | 'military_service'
  | 'praktika'
  | 'none';

const BUSINESS_TRIP_CHIP_GRADIENT =
  'linear-gradient(120deg, rgba(72, 181, 163, 0.92) 0%, rgba(100, 149, 237, 0.88) 50%, rgba(130, 210, 170, 0.88) 100%)';

const MILITARY_SERVICE_CHIP_GRADIENT =
  'linear-gradient(135deg, #4a5d23 0%, #6b7c32 35%, #556b2f 55%, #3d4a1f 100%)';

const PRAKTIKA_CHIP_GRADIENT =
  'linear-gradient(135deg, rgba(138, 98, 210, 0.95) 0%, rgba(167, 130, 230, 0.9) 45%, rgba(186, 148, 240, 0.88) 100%)';

export interface MobileScheduleDay {
  date: Date;
  dateString: string;
  dateLabel: string;
  weekdayLabel: string;
  status: MobileScheduleStatus;
  startTime?: string;
  endTime?: string;
  breakStartTime?: string;
  breakEndTime?: string;
  totalHours?: number;
  notes?: string;
  praktikaStartTime?: string;
  praktikaEndTime?: string;
  praktikaTotalHours?: number;
  excursions?: Array<{ title: string; startTime?: string; endTime?: string }>;
  edukacijos?: Array<{ title: string; startTime?: string; endTime?: string }>;
  planetarium?: Array<{ title: string; startTime?: string; endTime?: string }>;
}

interface MobileScheduleProps {
  days: MobileScheduleDay[];
  monthTitle: string;
  monthPlannedHours: number;
  monthHoursBalance: number | null;
  focusDate: Date;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
}

function placeholderDay(dateObj: Date): MobileScheduleDay {
  return {
    date: dateObj,
    dateString: formatDateLocal(dateObj),
    dateLabel: dateObj.toLocaleDateString('lt-LT', { day: '2-digit', month: '2-digit' }),
    weekdayLabel: dateObj.toLocaleDateString('lt-LT', { weekday: 'long' }),
    status: 'none',
  };
}

/** Always return Mon–Sun for the week containing focusDate (pad missing days). */
function buildVisibleWeek(days: MobileScheduleDay[], focusDate: Date): MobileScheduleDay[] {
  const monday = startOfWeekMonday(focusDate);
  const sunday = addDaysLocal(monday, 6);
  const byKey = new Map(days.map((d) => [d.dateString, d]));
  return eachLocalDay(monday, sunday).map((d) => byKey.get(formatDateLocal(d)) ?? placeholderDay(d));
}

const MobileSchedule: React.FC<MobileScheduleProps> = ({
  days,
  monthTitle,
  monthPlannedHours,
  monthHoursBalance,
  focusDate,
  onPrevWeek,
  onNextWeek,
  onToday
}) => {
  const { t } = useTranslation();

  const todayIso = formatDateLocal(new Date());

  const visibleDays = React.useMemo(
    () => buildVisibleWeek(days, focusDate),
    [days, focusDate]
  );

  const getStatusChip = (status: MobileScheduleDay['status']) => {
    if (status === 'none') {
      return (
        <Chip
          label={t('schedule.noEntry', 'Nėra įrašo')}
          size="small"
          variant="outlined"
          color="default"
        />
      );
    }

    const label =
      status === 'working'
        ? t('schedule.statusWorking', 'Darbo diena')
        : status === 'day_off'
        ? t('schedule.statusDayOff', 'Atostogos / Poilsio diena')
        : status === 'remote'
        ? t('schedule.statusRemote', 'Nuotolinis darbas')
        : status === 'sick_leave'
        ? t('schedule.statusSick', 'Liga')
        : status === 'mothers_day'
        ? t('schedule.statusMothersDay', 'Mamadienis')
        : status === 'rest_day'
        ? t('schedule.statusRestDay', 'Poilsio diena')
        : status === 'state_holiday'
        ? t('schedule.statusStateHoliday', 'Šventinė diena')
        : status === 'business_trip'
        ? t('schedule.statusBusinessTrip', 'Komandiruotė')
        : status === 'military_service'
        ? t('schedule.statusMilitaryService', 'Karo tarnyba')
        : status === 'praktika'
        ? t('schedule.statusPraktika', 'Praktika')
        : status;

    if (status === 'business_trip') {
      return (
        <Chip
          label={label}
          size="small"
          sx={{ background: BUSINESS_TRIP_CHIP_GRADIENT, color: 'black' }}
        />
      );
    }

    if (status === 'military_service') {
      return (
        <Chip
          label={label}
          size="small"
          sx={{ background: MILITARY_SERVICE_CHIP_GRADIENT, color: '#f5f5dc', fontWeight: 600 }}
        />
      );
    }

    if (status === 'praktika') {
      return (
        <Chip
          label={label}
          size="small"
          sx={{ background: PRAKTIKA_CHIP_GRADIENT, color: 'black', fontWeight: 600 }}
        />
      );
    }

    const color: 'default' | 'success' | 'warning' | 'info' | 'error' =
      status === 'working'
        ? 'success'
        : status === 'remote'
        ? 'info'
        : status === 'day_off' || status === 'rest_day' || status === 'state_holiday'
        ? 'default'
        : status === 'sick_leave' || status === 'mothers_day'
        ? 'warning'
        : 'default';

    return <Chip label={label} size="small" color={color} />;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('schedule.mobileTitle', 'Mobilus grafikas')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {monthTitle}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              label={t('schedule.mobilePlannedHours', {
                defaultValue: 'Planuojama: {{hours}} val.',
                hours: monthPlannedHours.toFixed(1),
              })}
              color="primary"
              size="small"
            />
            {monthHoursBalance != null && (
              <Chip
                label={t('schedule.mobileHoursBalance', {
                  defaultValue: 'Valandų balansas: {{hours}} val.',
                  hours: monthHoursBalance.toFixed(2),
                })}
                color="default"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              mb: 1.5,
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={onPrevWeek}>
                {t('schedule.prevWeek', 'Praeita savaitė')}
              </Button>
              <Button size="small" variant="outlined" onClick={onToday}>
                {t('schedule.today', 'Šiandien')}
              </Button>
              <Button size="small" variant="outlined" onClick={onNextWeek}>
                {t('schedule.nextWeek', 'Kita savaitė')}
              </Button>
            </Box>
          </Box>

          {visibleDays.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('schedule.noEntriesForPeriod', 'Nėra įrašų šiam laikotarpiui')}
            </Typography>
          ) : (
            <List disablePadding>
              {visibleDays.map((day, index) => {
                const isToday = day.dateString === todayIso;
                const hasShift = day.status !== 'rest_day' && !!day.startTime && !!day.endTime;

                return (
                  <React.Fragment key={day.dateString}>
                    <ListItem
                      sx={{
                        alignItems: 'flex-start',
                        borderRadius: 1,
                        mb: 0.5,
                        px: 1,
                        py: 1,
                        bgcolor: isToday ? 'rgba(76, 175, 80, 0.12)' : 'transparent',
                      }}
                    >
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            gap: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {day.dateLabel} · {day.weekdayLabel}
                            {isToday && ` · ${t('schedule.today', 'Šiandien')}`}
                          </Typography>
                          {getStatusChip(day.status)}
                        </Box>

                        {hasShift && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Chip
                                label={`${day.startTime} – ${day.endTime}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {day.breakStartTime && day.breakEndTime && (
                                <Chip
                                  label={t('schedule.breakChip', {
                                    defaultValue: `Pertrauka ${day.breakStartTime}–${day.breakEndTime}`,
                                  })}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {day.totalHours != null && day.totalHours > 0 && day.status !== 'rest_day' && (
                                <Chip
                                  label={t('schedule.hoursChip', {
                                    defaultValue: '{{hours}} val.',
                                    hours: day.totalHours.toFixed(1),
                                  })}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              )}
                            </Box>

                            {day.notes && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.25 }}
                              >
                                {t('schedule.noteLabel', 'Pastaba')}: {day.notes}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {day.praktikaStartTime && day.praktikaEndTime && (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                            <Chip
                              label={t('schedule.statusPraktika', 'Praktika')}
                              size="small"
                              sx={{ background: PRAKTIKA_CHIP_GRADIENT, color: 'black', fontWeight: 600 }}
                            />
                            <Chip
                              label={`${day.praktikaStartTime} – ${day.praktikaEndTime}`}
                              size="small"
                              variant="outlined"
                              sx={{ borderColor: 'rgba(147, 112, 219, 0.7)' }}
                            />
                            {day.praktikaTotalHours != null && day.praktikaTotalHours > 0 && (
                              <Chip
                                label={t('schedule.hoursChip', {
                                  defaultValue: '{{hours}} val.',
                                  hours: day.praktikaTotalHours.toFixed(1),
                                })}
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: 'rgba(147, 112, 219, 0.7)' }}
                              />
                            )}
                          </Box>
                        )}

                        {((day.excursions && day.excursions.length > 0) ||
                          (day.edukacijos && day.edukacijos.length > 0) ||
                          (day.planetarium && day.planetarium.length > 0)) && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.25,
                              mt: hasShift ? 0.5 : 0.25,
                            }}
                          >
                            {day.excursions && day.excursions.length > 0 && (
                              <>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontWeight: 600, mt: 0.25 }}
                                >
                                  {t('schedule.mobileExcursionsHeading')}
                                </Typography>
                                {day.excursions.map((ex, i) => (
                                  <Typography
                                    key={`ex-${day.dateString}-${i}`}
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ pl: 0.5 }}
                                  >
                                    {ex.title}
                                    {ex.startTime && ex.endTime
                                      ? ` · ${ex.startTime.slice(0, 5)}–${ex.endTime.slice(0, 5)}`
                                      : ''}
                                  </Typography>
                                ))}
                              </>
                            )}
                            {day.edukacijos && day.edukacijos.length > 0 && (
                              <>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontWeight: 600, mt: day.excursions?.length ? 0.5 : 0.25 }}
                                >
                                  {t('schedule.mobileEducationsHeading')}
                                </Typography>
                                {day.edukacijos.map((ed, i) => (
                                  <Typography
                                    key={`ed-${day.dateString}-${i}`}
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ pl: 0.5 }}
                                  >
                                    {ed.title}
                                    {ed.startTime && ed.endTime
                                      ? ` · ${ed.startTime.slice(0, 5)}–${ed.endTime.slice(0, 5)}`
                                      : ''}
                                  </Typography>
                                ))}
                              </>
                            )}
                            {day.planetarium && day.planetarium.length > 0 && (
                              <>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontWeight: 600,
                                    mt:
                                      day.excursions?.length || day.edukacijos?.length ? 0.5 : 0.25
                                  }}
                                >
                                  {t('schedule.mobilePlanetariumHeading', 'Planetariumas')}
                                </Typography>
                                {day.planetarium.map((pl, i) => (
                                  <Typography
                                    key={`pl-${day.dateString}-${i}`}
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ pl: 0.5 }}
                                  >
                                    {pl.title}
                                    {pl.startTime
                                      ? ` · ${pl.startTime.slice(0, 5)}${
                                          pl.endTime ? `–${pl.endTime.slice(0, 5)}` : ''
                                        }`
                                      : ''}
                                  </Typography>
                                ))}
                              </>
                            )}
                          </Box>
                        )}
                      </Box>
                    </ListItem>
                    {index < visibleDays.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MobileSchedule;
